import { InboxSortMode, sortInboxNotifications } from '@bariendo/lib/inbox-sort';
import { hideFutureInboxTasks } from '@bariendo/lib/inbox-tasks';
import { useDebouncedValue } from '@mantine/hooks';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useCallback, useMemo, useState } from 'react';
import { useLiveGraphQL } from '../../hooks/useLiveGraphQL';
import {
  buildNotificationCombinedFilter,
  buildNotificationQueryParamsWithFilters,
} from '@/services/notification-queries';
import { getNotificationItemsFromTasks } from './services';
import { TypeFilter } from './types';
import { NOTIFICATION_TASKS_QUERY, NotificationTask, ROLE_TASKS_QUERY } from './graphql';
import useLoadMoreOnScroll from '@/hooks/useLoadMoreOnScroll';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLocalDayStart } from '@/hooks/useLocalDayStart';
import { TaskGroupIdentifier } from '@bariendo/lib/constants';
import { getReferenceString } from '@medplum/core';

export interface NotificationTableOptions {
  notificationStatus: string | string[];
  selectedProvider?: Practitioner | null;
  sortMode: InboxSortMode;
  /**
   * Whether clinical tasks due after today stay in the list. Defaults to
   * true; the inbox passes false (BAR-2679) so it opens on today's work.
   * Ignored while a search term is active: a search must find the task it
   * names whatever its due date.
   */
  showFutureTasks?: boolean;
  /**
   * Fetch the owner's clinical tasks (orbit-provider-task) in full, in their
   * own query, instead of riding the 30-row notification pages. The due-date
   * sort and the future filter are client-side, so they are only right over the
   * whole set: paged in by creation date, a task due today but created weeks
   * ago sat behind newer rows until the reader scrolled to it (prod, 2026-10-22:
   * one provider's overdue tasks were at rows 78-173). Open tasks only — the
   * Archive leaves this off, since the completed history only grows.
   */
  loadAllClinicalTasks?: boolean;
}

const INBOX_PAGE_SIZE = 30;

export function useNotificationTable({
  notificationStatus,
  selectedProvider,
  sortMode,
  showFutureTasks = true,
  loadAllClinicalTasks = false,
}: NotificationTableOptions) {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;

  const [typeFilter, setTypeFilter] = useState<TypeFilter>(TypeFilter.ALL);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300);
  // The debounced term is what the queries see, so it is also what decides
  // whether the future filter is suspended (and whether the page shows its toggle).
  const isSearching = Boolean(debouncedSearchTerm?.trim());
  const hidingFutureTasks = !showFutureTasks && !isSearching;
  // Advances at local midnight and on tab return, so "due after today" is
  // re-evaluated instead of frozen at the moment the list was first built.
  const dayStart = useLocalDayStart();
  // Build query variables using the notification status
  const queryVariables = useMemo(() => {
    if (!profile) return undefined;

    const params = buildNotificationQueryParamsWithFilters(profile, notificationStatus, {
      typeFilter: typeFilter !== TypeFilter.ALL ? typeFilter : undefined,
      searchTerm: debouncedSearchTerm?.trim() || undefined,
      sortParameter: '-authored-on',
      selectedProvider: selectedProvider || undefined,
    });

    const combinedFilter = buildNotificationCombinedFilter(params);

    return {
      group_identifier: params['group-identifier'] ?? '',
      owner: params.owner ?? '',
      status: params.status ?? '',
      // Omitted, not undefined, when there is no filter: extractSubscriptionCriteria
      // copies any present key, `_filter=undefined` included, and useLiveGraphQL
      // refuses criteria carrying _filter — so the list never subscribed at all.
      // Without the key, the unfiltered All view gets live updates.
      ...(combinedFilter ? { _filter: combinedFilter } : {}),
      _sort: params._sort ?? '-authored-on',
    };
  }, [profile, notificationStatus, typeFilter, debouncedSearchTerm, selectedProvider]);

  // Category views (SMS, Calls, Fax, …) filter on focus:Communication, which a
  // clinical task never has, so the clinical query only runs for All and Tasks.
  const clinicalQueryEnabled =
    loadAllClinicalTasks && (typeFilter === TypeFilter.TASKS || typeFilter === TypeFilter.ALL);

  // With the clinical query on, the paged query keeps the notification groups
  // only; otherwise the same clinical task would come back from both.
  const pagedQueryVariables = useMemo(() => {
    if (!queryVariables || !clinicalQueryEnabled) return queryVariables;
    return {
      ...queryVariables,
      group_identifier: queryVariables.group_identifier
        .split(',')
        .filter((group: string) => group !== TaskGroupIdentifier.ORBIT_PROVIDER_TASK)
        .join(','),
    };
  }, [queryVariables, clinicalQueryEnabled]);

  // MUST be {} (not undefined) when disabled — see roleQueryVariables below.
  const clinicalQueryVariables = useMemo(() => {
    if (!queryVariables || !clinicalQueryEnabled) return {};
    return { ...queryVariables, group_identifier: TaskGroupIdentifier.ORBIT_PROVIDER_TASK };
  }, [queryVariables, clinicalQueryEnabled]);

  const {
    data: notificationTasks,
    loading,
    loadingMore,
    refreshing,
    hasMore,
    loadMore,
    refresh,
  } = useLiveGraphQL<NotificationTask>(NOTIFICATION_TASKS_QUERY, pagedQueryVariables, {
    enablePagination: true,
    pageSize: INBOX_PAGE_SIZE,
  });

  // No enablePagination on purpose: useLiveGraphQL then pages until a short
  // page comes back, i.e. every open clinical task the owner holds. That set
  // is the owner's own to-do list (~120 at the busiest in prod), not a feed.
  const {
    data: clinicalTasks,
    loading: clinicalLoading,
    refresh: refreshClinicalTasks,
  } = useLiveGraphQL<NotificationTask>(NOTIFICATION_TASKS_QUERY, clinicalQueryVariables, {
    pageSize: 100,
    pauseLiveUpdates: !clinicalQueryEnabled,
  });

  // ROLE-assigned clinical tasks: CareTeam-owned, or unowned when the patient
  // has no CareTeam, so the owner-scoped query above can never return them.
  // The performer-scoped query has no user parameter — every holder of the
  // role sees the same rows, and one completion (status leaves the open set)
  // removes it for everyone.
  // Only when viewing one's OWN inbox: monitoring another provider would
  // need their role, which we don't have here.
  const { currentUserRole } = useCurrentUser();
  // Own inbox only: monitoring another provider would need THEIR role, which
  // the client doesn't have.
  const roleQueryEnabled =
    (typeFilter === TypeFilter.TASKS || typeFilter === TypeFilter.ALL) &&
    Boolean(currentUserRole) &&
    Boolean(profile) &&
    !(selectedProvider && getReferenceString(selectedProvider) !== getReferenceString(profile));

  // MUST be {} (not undefined) when disabled: useLiveGraphQL only skips
  // fetching on an EMPTY OBJECT, and undefined variables make
  // extractSubscriptionCriteria fall through to the bare resource type —
  // i.e. a websocket subscription to every Task write in the project.
  // pauseLiveUpdates belts-and-braces that by blanking the criteria.
  const roleQueryVariables = useMemo(() => {
    if (!roleQueryEnabled) {
      return {};
    }
    const term = debouncedSearchTerm?.trim();
    return {
      group_identifier: TaskGroupIdentifier.ORBIT_PROVIDER_TASK,
      performer: currentUserRole as string,
      status: Array.isArray(notificationStatus) ? notificationStatus.join(',') : notificationStatus,
      // Same _filter the owner query uses, so typing in the inbox search
      // narrows role tasks too — otherwise they stayed visible, unfiltered,
      // next to results that did match.
      ...(term ? { _filter: `(code co "${term}" or patient.name co "${term}")` } : {}),
      _sort: '-authored-on',
    };
  }, [roleQueryEnabled, currentUserRole, notificationStatus, debouncedSearchTerm]);

  const { data: roleTasks, refresh: refreshRoleTasks } = useLiveGraphQL<NotificationTask>(
    ROLE_TASKS_QUERY,
    roleQueryVariables,
    // Unpaged only alongside loadAllClinicalTasks (the Inbox, open statuses):
    // the future filter and due-date sort need the whole set here too, or a
    // role task created long ago and due today falls off the first page.
    // Elsewhere (the Archive) enablePagination MUST stay set: without it the
    // query fetches the entire completed history across all patients.
    { enablePagination: !loadAllClinicalTasks, pageSize: 100, pauseLiveUpdates: !roleQueryEnabled }
  );

  const notifications = useMemo(() => {
    const pagedTasks = Array.isArray(notificationTasks) ? notificationTasks : [];
    let fullyLoadedTasks = [
      ...(clinicalQueryEnabled && Array.isArray(clinicalTasks) ? clinicalTasks : []),
      ...(roleQueryEnabled && Array.isArray(roleTasks) ? roleTasks : []),
    ];

    // Newest first is a chronological feed, and the paged notifications only
    // reach back to their oldest loaded row. A fully loaded task older than that
    // would sit ahead of weeks of notifications not yet fetched, then slide down
    // with every page. Hold it back until the pages reach its date.
    const oldestPaged = pagedTasks.at(-1)?.authoredOn;
    if (sortMode === InboxSortMode.NEWEST_FIRST && hasMore && oldestPaged) {
      const cutoff = Date.parse(oldestPaged);
      fullyLoadedTasks = fullyLoadedTasks.filter((t) => !t.authoredOn || Date.parse(t.authoredOn) >= cutoff);
    }

    const seen = new Set<string | undefined>();
    const mergedTasks: NotificationTask[] = [];
    for (const task of [...pagedTasks, ...fullyLoadedTasks]) {
      if (!seen.has(task.id)) {
        seen.add(task.id);
        mergedTasks.push(task);
      }
    }
    if (mergedTasks.length === 0) return [];

    const items = getNotificationItemsFromTasks(mergedTasks);
    return sortInboxNotifications(hidingFutureTasks ? hideFutureInboxTasks(items, dayStart) : items, sortMode);
  }, [
    notificationTasks,
    hasMore,
    clinicalTasks,
    clinicalQueryEnabled,
    roleTasks,
    roleQueryEnabled,
    sortMode,
    hidingFutureTasks,
    dayStart,
  ]);

  // Handle infinite scroll
  const handleScroll = useLoadMoreOnScroll({ hasMore, loadingMore, loading, loadMore });

  // Refresh every source: completing a role or clinical task from the inbox
  // must drop the row whichever query it came from.
  const refreshAll = useCallback(() => {
    refresh();
    refreshClinicalTasks();
    refreshRoleTasks();
  }, [refresh, refreshClinicalTasks, refreshRoleTasks]);

  return {
    medplum,
    typeFilter,
    setTypeFilter,
    searchTerm,
    setSearchTerm,
    isSearching,
    notifications,
    loading: (loading || (clinicalQueryEnabled && clinicalLoading)) && !refreshing,
    loadingMore,
    hasMore,
    refresh: refreshAll,
    handleScroll,
  };
}
